<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useCreateWorkspace } from '@/features/workspaces/mutations/useCreateWorkspace'
import { useWorkspaceStore } from '@/stores/workspace'

const open = defineModel<boolean>('open', { required: true })

const { t } = useI18n()
const workspaceStore = useWorkspaceStore()
const createWorkspace = useCreateWorkspace()

const name = ref('')

const canSubmit = computed(
  () => name.value.trim() !== '' && workspaceStore.currentOrganizationId !== null,
)

function handleSubmit(): void {
  if (!canSubmit.value || workspaceStore.currentOrganizationId === null) return

  createWorkspace.mutate(
    {
      organizationId: Number(workspaceStore.currentOrganizationId),
      name: name.value.trim(),
    },
    {
      onSuccess: () => {
        open.value = false
        name.value = ''
      },
    },
  )
}
</script>

<template>
  <Dialog v-model:open="open">
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{{ t('workspaces.createDialog.title') }}</DialogTitle>
        <DialogDescription>
          {{
            t('workspaces.createDialog.description', {
              organization: workspaceStore.currentOrganization?.name ?? '',
            })
          }}
        </DialogDescription>
      </DialogHeader>

      <div class="space-y-1.5">
        <label for="workspace-name" class="text-sm font-medium">{{
          t('workspaces.createDialog.name')
        }}</label>
        <Input
          id="workspace-name"
          v-model="name"
          :placeholder="t('workspaces.createDialog.namePlaceholder')"
          @keydown.enter="handleSubmit"
        />
      </div>

      <DialogFooter>
        <Button variant="outline" @click="open = false">{{ t('common.cancel') }}</Button>
        <Button :disabled="!canSubmit || createWorkspace.isPending.value" @click="handleSubmit">
          {{ t('workspaces.createDialog.submit') }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
